import React, { useEffect, useRef } from "react";

interface TooltipScrollContainerProps {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  [key: string]: any; // Allow any other div props
}

const TooltipScrollContainer: React.FC<TooltipScrollContainerProps> = ({
  children,
  className = "",
  style = {},
  ...otherProps
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      // Method 1: Dispatch custom event for components listening to it
      const closeTooltipsEvent = new CustomEvent("closeAllTooltips");
      document.dispatchEvent(closeTooltipsEvent);

      // Method 2: Force close MUI tooltips by hiding them temporarily
      const tooltips = document.querySelectorAll('[role="tooltip"]');
      tooltips.forEach((tooltip) => {
        const tooltipElement = tooltip as HTMLElement;
        if (tooltipElement && tooltipElement.style.display !== "none") {
          tooltipElement.style.display = "none";
          // Restore after a short delay to allow MUI to handle state properly
          setTimeout(() => {
            if (tooltipElement.style.display === "none") {
              tooltipElement.style.display = "";
            }
          }, 50);
        }
      });

      // Method 3: Trigger mouse leave events on tooltip triggers inside this container
      const container = containerRef.current;
      if (container) {
        // Find all elements that might have tooltips
        const possibleTooltipTriggers = container.querySelectorAll(
          "[data-mui-internal-clone-element], [title], .MuiTooltip-root, .MuiFormControlLabel-root"
        );

        possibleTooltipTriggers.forEach((trigger) => {
          const mouseLeaveEvent = new MouseEvent("mouseleave", {
            bubbles: true,
            cancelable: true,
          });
          trigger.dispatchEvent(mouseLeaveEvent);
        });
      }
    };

    const container = containerRef.current;
    if (container) {
      // Add scroll listener to this container
      container.addEventListener("scroll", handleScroll);

      return () => {
        container.removeEventListener("scroll", handleScroll);
      };
    }
  }, []);

  return (
    <div ref={containerRef} className={className} style={style} {...otherProps}>
      {children}
    </div>
  );
};

export default TooltipScrollContainer;
